/**
 * The project list: every project room, active first, with its status line and how much
 * checklist work remains. Creating a project also creates its register entity and wiki page.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError, api, type ProjectListItem, type Space } from "../../api";
import { Dialog } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { Badge, Button, Card, Field, Num } from "../../components/primitives";
import { SkeletonText } from "../../components/Skeleton";
import { useToast } from "../../components/toast";
import { TemplateDialog } from "./TemplateDialog";

export const STATUS_LABELS: Record<string, string> = {
  active: "Aktivt",
  paused: "Pausat",
  closed: "Avslutat",
};

export function ProjectStatusBadge({ status }: { status: string }) {
  if (status === "active") {
    return <Badge status="pass">{STATUS_LABELS.active}</Badge>;
  }
  if (status === "paused") {
    return <Badge status="warn">{STATUS_LABELS.paused}</Badge>;
  }
  return <span className="text-small text-muted">{STATUS_LABELS[status] ?? status}</span>;
}

export function ProjektPage() {
  const navigate = useNavigate();
  const showToast = useToast();

  const [projects, setProjects] = useState<ProjectListItem[] | null>(null);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [name, setName] = useState("");
  const [spaceId, setSpaceId] = useState("");

  useEffect(() => {
    void api.projects().then(setProjects);
    void api.spaces().then((loaded) => {
      const shared = loaded.filter((space) => space.kind === "shared");
      setSpaces(shared);
      setSpaceId((current) => current || (shared[0]?.id ?? ""));
    });
  }, []);

  const create = useCallback(async () => {
    if (!name.trim() || !spaceId) {
      return;
    }
    try {
      const created = await api.createProject({ name: name.trim(), space_id: spaceId });
      setCreateOpen(false);
      setName("");
      navigate(`/projekt/${created.id}`);
    } catch (error) {
      showToast({
        message: error instanceof ApiError ? error.message : "Projektet kunde inte skapas.",
        tone: "fail",
      });
    }
  }, [name, spaceId, navigate, showToast]);

  if (projects === null) {
    return <SkeletonText lines={5} />;
  }

  return (
    <div className="grid content-start gap-s6">
      <div className="flex flex-wrap items-center gap-s4">
        <h1 className="text-h2 font-bold text-ink">Projekt</h1>
        <div className="ml-auto flex items-center gap-s4">
          <Button variant="ghost" onClick={() => setTemplatesOpen(true)}>
            Checklistmallar
          </Button>
          <Button variant="cta" onClick={() => setCreateOpen(true)}>
            Nytt projekt
          </Button>
        </div>
      </div>

      {projects.length === 0 ? (
        <EmptyState
          title="Inga projekt ännu"
          description="Skapa det första projektrummet. Det får en fast framsida med status, löften, åtaganden och antaganden — och en egen wikisida."
          action={
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              Nytt projekt
            </Button>
          }
        />
      ) : (
        <ul className="grid gap-s4">
          {projects.map((project) => (
            <li key={project.id}>
              <Card
                role="button"
                tabIndex={0}
                className="cursor-pointer transition-[box-shadow] duration-control ease-nb hover:shadow-dialog"
                onClick={() => navigate(`/projekt/${project.id}`)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    navigate(`/projekt/${project.id}`);
                  }
                }}
              >
                <div className="flex flex-wrap items-center gap-s4">
                  <span className="text-title font-medium text-ink">{project.name}</span>
                  <ProjectStatusBadge status={project.status} />
                  {project.open_checklist_items > 0 && (
                    <span className="ml-auto text-small text-muted">
                      <Num>{project.open_checklist_items}</Num> punkter kvar
                    </span>
                  )}
                </div>
                {project.status_note && (
                  <p className="mt-s2 text-body text-muted">{project.status_note}</p>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={createOpen}
        title="Nytt projekt"
        subtitle="Framsida, register och wikisida skapas"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button onClick={() => setCreateOpen(false)}>Avbryt</Button>
            <Button variant="primary" onClick={() => void create()} disabled={!name.trim()}>
              Skapa
            </Button>
          </>
        }
      >
        <form
          className="grid gap-s5"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <Field
            label="Projektnamn"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
          <label className="grid gap-s2">
            <span className="nb-overline border-0 pb-0">Utrymme för wikisidan</span>
            <select
              className="nb-field"
              value={spaceId}
              onChange={(event) => setSpaceId(event.target.value)}
            >
              {spaces.map((space) => (
                <option key={space.id} value={space.id}>
                  {space.name}
                </option>
              ))}
            </select>
          </label>
        </form>
      </Dialog>

      <TemplateDialog open={templatesOpen} onClose={() => setTemplatesOpen(false)} />
    </div>
  );
}
